import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { supabase } from '@/integrations/backend/client';
import { useSubscription, PLAN_DETAILS, PlanId } from '@/contexts/SubscriptionContext';
import { toast } from 'sonner';
import { Gift, Sparkles, CheckCircle2 } from 'lucide-react';

interface PromoCodeRedeemerProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When true, renders only the Dialog without a trigger button */
  controlled?: boolean;
}

export function PromoCodeRedeemer({ open: controlledOpen, onOpenChange, controlled }: PromoCodeRedeemerProps = {}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlled ? (controlledOpen ?? false) : internalOpen;
  const setOpen = (v: boolean) => {
    if (controlled) {
      onOpenChange?.(v);
    } else {
      setInternalOpen(v);
    }
  };
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [redeemed, setRedeemed] = useState<{ planId: PlanId; months: number } | null>(null);
  const { checkSubscription } = useSubscription();

  const handleRedeem = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      toast.error('Digite um código promocional');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('redeem-promo', {
        body: { code: trimmed },
      });

      if (error) {
        // Try to extract message from error context
        const anyErr = error as any;
        const body = anyErr?.context?.body;
        if (typeof body === 'string') {
          try {
            const parsed = JSON.parse(body);
            if (parsed?.error) {
              toast.error(parsed.error);
              return;
            }
          } catch { /* ignore */ }
        }
        toast.error('Não foi possível resgatar o código.');
        return;
      }

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      if (data?.success) {
        setRedeemed({ planId: data.plan_id as PlanId, months: data.months });
        await checkSubscription();
        toast.success('Código resgatado com sucesso!');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao resgatar código.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setCode('');
    setRedeemed(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else setOpen(true); }}>
      {!controlled && (
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <Gift className="h-4 w-4" />
            Promo Code
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Gift className="h-5 w-5 text-primary" />
            Resgatar Código Promocional
          </DialogTitle>
        </DialogHeader>

        {redeemed ? (
          <div className="text-center py-6 space-y-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Plano ativado!</h3>
              <p className="text-muted-foreground mt-1">
                Você agora tem acesso ao plano{' '}
                <span className="font-semibold text-foreground">
                  {PLAN_DETAILS[redeemed.planId]?.name || redeemed.planId}
                </span>{' '}
                por {redeemed.months} {redeemed.months === 1 ? 'mês' : 'meses'}.
              </p>
            </div>
            <Button onClick={handleClose} className="mt-2">Fechar</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="promo-code">Código</Label>
              <Input
                id="promo-code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="QLIVAR-XXXXXX"
                maxLength={30}
                onKeyDown={(e) => { if (e.key === 'Enter') handleRedeem(); }}
              />
            </div>
            <Button
              className="w-full gap-2"
              onClick={handleRedeem}
              disabled={loading || !code.trim()}
            >
              {loading ? (
                'Resgatando...'
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Resgatar
                </>
              )}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Insira o código promocional que você recebeu para ativar seu plano.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
